<template>
  <mi-dialog
    :model-value="modelValue"
    :inner-loading="areValidationSettingsSaving"
    :persistent="areValidationSettingsSaving"
    class="validation-settings-dialog"
    no-route-dismiss
    @before-show="setSettingsDialogData"
    @update:model-value="emitUpdateModelValue"
  >
    <h5 class="q-mt-none q-mb-lg"> Validation settings </h5>

    <!-- Tabs -->
    <mi-tabs v-model="activeValidationType">
      <mi-tab
        v-for="(settingValue, settingName) in validationSettings"
        :key="settingName"
        :name="settingName"
        :label="settingValue.label"
      ></mi-tab>
    </mi-tabs>

    <!-- Column headers + active counter -->
    <div
      v-if="activeValidationType === 'bom' && currentSettings.bom?.length"
      class="validation-settings-header row q-pt-md"
    >
      <div class="validation-settings-header__toggle validation-settings-counter">
        Active ({{ activeBomSettingsCount }}/{{ validationSettingsLimit }})
      </div>
      <div class="validation-settings-header__name validation-settings-header__label"> Validation name </div>
      <div class="validation-settings-header__pp validation-settings-header__label"> Planning period </div>
      <div class="col validation-settings-header__label"> Precondition (optional) </div>
    </div>

    <mi-form
      class="q-pt-xs validation-settings-form"
      @validation-error="goToValidationErrorTab"
      @submit="saveValidationSettings"
    >
      <mi-tab-panels ref="validationTabPanels" v-model="activeValidationType" keep-alive>
        <!-- BOM panel -->
        <product-model-settings-tab
          name="bom"
          :settings="currentSettings.bom"
          :active-limit="validationSettingsLimit"
          :add-precondition="addPrecondition"
          :remove-precondition="removePrecondition"
          :invalid-choices="invalidChoices"
          :product-model="selectedProductModel"
          @click:remove="removeValidationSetting"
          @toggle:active="toggleValidationSettingActive"
        ></product-model-settings-tab>

        <!-- Not usable choices panel -->
        <product-model-settings-tab
          name="notUsableChoices"
          :settings="currentSettings.notUsableChoices"
          @click:remove="removeValidationSetting"
        ></product-model-settings-tab>
      </mi-tab-panels>

      <div class="validation-settings-footer flex items-center q-pt-xl q-pb-sm">
        <!-- Add new setting button -->
        <mi-btn
          class="validation-settings-footer__add"
          icon="plus-circle"
          icon-type
          icon-size="19px"
          flat
          @click="addValidationSetting"
        >
          Add new
        </mi-btn>

        <mi-space></mi-space>

        <mi-btn
          v-show="canCreate"
          class="validation-settings-footer__save"
          color="accent"
          type="submit"
        >
          {{ activeValidationType === 'bom' ? 'Save AutoBOM configurations' : 'Apply' }}
        </mi-btn>
      </div>
    </mi-form>
  </mi-dialog>
</template>

<script>
  import cloneDeep from 'lodash.clonedeep'
  import { createNamespacedHelpers } from 'vuex'

  import { saveProductModelSettings } from '@/api'
  import notify from '@/utils/notify'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import { PROD_MODEL_SAVED_VALIDATION } from '@/utils/analytics/constants'

  import { canAccess } from '@/utils/accessCheck'
  import ProductModelSettingsTab from './ProductModelSettingsTab.vue'

  const { mapGetters } = createNamespacedHelpers('product-model')

  export default {
    name: 'ProductModelSettings',
    components: { ProductModelSettingsTab },
    props: {
      modelValue: {
        type: Boolean,
        required: true
      },
      validationSettings: {
        type: Object,
        required: true
      }
    },
    emits: ['update:model-value'],
    data: () => ({
      areValidationSettingsSaving: false,
      activeValidationType: '',
      currentSettings: {},
      canCreate: true,
      validationSettingsLimit: 15,
      shouldDisplay: true,
      invalidChoices: new Set()
    }),
    computed: {
      ...mapGetters(['selectedProductModel']),
      activeBomSettingsCount() {
        return (this.currentSettings.bom || []).filter(setting => setting.isActive).length
      }
    },
    created() {
      this.canCreate = (this.activeValidationType === 'bom')
        ? canAccess('CREATE_VALIDATION_SETTINGS')
        : canAccess('CREATE_NOT_USABLE_CHOICES_SETTINGS')
    },
    methods: {
      async addPrecondition(conditions, fieldIndex) {
        // eslint-disable-next-line security/detect-object-injection
        this.currentSettings[this.activeValidationType][fieldIndex].preCondition = conditions
      },
      async removePrecondition(choice, fieldIndex) {
        // eslint-disable-next-line security/detect-object-injection
        this.currentSettings[this.activeValidationType][fieldIndex].preCondition.splice(choice.index, 1)
      },
      async saveValidationSettings() {
        const params = {
          encodedBusinessName: this.selectedProductModel.encodedBusinessName,
          bomSettings: this.currentSettings.bom,
          invalidChoicesSettings: this.currentSettings.notUsableChoices
        }

        // Make sure range 'to' value, equals 'from' value
        this.planningPeriodCopyFrom(params.bomSettings)
        this.planningPeriodCopyFrom(params.invalidChoicesSettings)

        this.areValidationSettingsSaving = true

        try {
          await saveProductModelSettings(params)

          this.emitUpdateModelValue()

          notify({
            title: 'Saved',
            content: `Settings for "${ this.selectedProductModel.businessName }" product model were successfully saved`,
            type: 'positive'
          })

          this.invalidChoices = new Set()

          // Analytics
          recordAnalytics(
            PROD_MODEL_SAVED_VALIDATION,
            {
              productModelBusinessName: this.selectedProductModel.businessName
            }
          )
        }
        catch (e) {
          this.invalidChoices = new Set(
            e.response.data.bomSettings.flatMap(setting => setting.preCondition.invalidChoices.map(item => item.id2))
          )
        }
        finally {
          this.areValidationSettingsSaving = false
        }
      },
      planningPeriodCopyFrom(settings) {
        settings.forEach(item => {
          item.checkRange.to = item.checkRange.from
        })
      },
      addValidationSetting() {
        const canBeActive = this.activeValidationType !== 'bom'
          || this.activeBomSettingsCount < this.validationSettingsLimit

        const emptySetting = {
          name: '',
          isActive: canBeActive,
          checkRange: { from: '', to: '' }
        }

        this.currentSettings[this.activeValidationType]?.push(emptySetting)
      },
      removeValidationSetting(settingIdx) {
        this.currentSettings[this.activeValidationType]?.splice(settingIdx, 1)
      },
      toggleValidationSettingActive({ index, isActive }) {
        // eslint-disable-next-line security/detect-object-injection
        const setting = this.currentSettings.bom?.[index]

        if (!setting) return

        setting.isActive = isActive
      },
      setSettingsDialogData() {
        [this.activeValidationType] = Object.keys(this.validationSettings)

        Object.keys(this.validationSettings).forEach(key => {
          // eslint-disable-next-line security/detect-object-injection
          this.currentSettings[key] = cloneDeep(this.validationSettings[key].items)
        })
      },
      goToValidationErrorTab(ref = {}) {
        if (ref.name in this.currentSettings) {
          this.$refs.validationTabPanels?.goTo(ref.name)
        }
      },
      emitUpdateModelValue(value = false) {
        this.$emit('update:model-value', value)
      }
    }
  }
</script>

<style lang="scss">
  .validation-settings-dialog .q-dialog__inner > .mi-card {
    min-width: 988px;
  }

  .validations-scroll {
    height: 200px;
  }

  .validation-settings-counter {
    font-family: $mi-typography-font-family-condensed;
    font-size: 14px;
    font-weight: 700;
    color: $mi-anthracite-800;
    letter-spacing: .2px;
  }

  .validation-settings-header {
    &__toggle {
      flex: 0 0 88px;
      width: 88px;
      white-space: nowrap;
    }

    &__name {
      flex: 0 0 200px;
      width: 200px;
    }

    &__pp {
      flex: 0 0 150px;
      width: 150px;
    }

    &__label {
      text-align-last: center;
      padding-bottom: 4px;
      font-family: $mi-typography-font-family-condensed;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: .2px;
      color: $mi-anthracite-800;
    }
  }

  .validation-settings-footer {
    &__add {
      color: $mi-red-500;
      font-weight: 700;
    }

    &__save {
      font-weight: 700;
    }
  }

  .validation-settings-item {
    margin-top: 2px;

    .validation-row-center {
      margin-top: 24px;
    }

    ::v-deep(.mi-btn) {
      opacity: 0;
      pointer-events: none;
      font-size: .85rem;
    }

    &:hover ::v-deep(.mi-btn) {
      opacity: 1;
      pointer-events: auto;
    }
  }
</style>
